import { showDialog } from "../message/messageSlice";
import { IMPORT_CLUB_TRIGGER, storeSignupContext } from "./signupContextPort";
/**
 * Records that the user chose to start a club import, and from where.
 *
 * Every club-import entry point funnels through here so the origin is captured
 * once, at the moment of intent, rather than at whichever login modal happens
 * to appear several screens later. Without it the recorded trigger for every
 * import signup is `login-required` from `/club-import-tutorial` — see the
 * flow walkthrough in events/signupTriggers.ts.
 *
 * Safe to call for logged-in users too: the context is only ever consumed on a
 * first-time login (userSlice clears it otherwise), so a returning user's
 * import writes a value nobody reads.
 */
export const noteClubImportIntent = (origin, featureId) => storeSignupContext(IMPORT_CLUB_TRIGGER, {
    section: origin.section,
    subsection: origin.subsection ?? featureId,
});
/**
 * Raises the club-import gate, naming the feature the user was reaching for.
 *
 * Also records the signup intent — the gate is the first moment we know both
 * that an import is wanted and what for, and both of the dialog's exits ("Import
 * My Club" and the "Log in" link) lead to a signup that would otherwise report
 * itself as a bare `login-required`.
 */
export const promptClubImport = (featureId, origin) => {
    if (origin)
        noteClubImportIntent(origin, featureId);
    /*
      The origin travels into the dialog as well as into the signup context: the
      dialog's "Import My Club" is an import CTA like any other, and the surface
      it was raised from is the answer to "where did this import come from" —
      which the dialog itself cannot see.
    */
    return showDialog({
        modalType: "club-import-gate",
        data: { featureId, origin },
    });
};
/**
 * Raised when the club is imported but the balance is spent: on a refusal
 * (the gate or a 402), or right as the last credit goes (`reason: "spent"`).
 *
 * `featureId` names the action, which is the whole point of the dialog: the
 * user needs to learn that this club-powered action costs a credit and that
 * everything else is still free. Optional because the interceptor can only
 * infer it from the request URL, and a wrong name is worse than none.
 */
export const promptOutOfCredits = (featureId, reason = "refused") => showDialog({ modalType: "out-of-credits", data: { featureId, reason } });
//# sourceMappingURL=clubImportPrompt.js.map